/*
  Methods to map regular schema.org openingHours 
  to a custom format used by the current backend
  see https://schema.org/OpeningHoursSpecification

  Enhanced with overnight hours support and 24:00 format handling.
  Refactored to use centralized utilities for better maintainability.

Key Features:
1. schema.org requires an entry for every day, even if closed.
2. Overnight intervals (spanning midnight) are properly handled and displayed on starting day
3. Supports 24:00 format for midnight next day (86400000 ms)
4. Merges overnight parts so intervals appear on the day they start
5. Custom times use milliseconds since midnight, schema.org uses "HH:MM" strings
6. Day names mapping: full names vs three-letter keys

Overnight Hours Logic:
- Times like "23:00-03:00" are kept together and shown on the starting day
- 24:00 represents midnight of the next day
- Visual indicators help users understand overnight periods
*/

import { formatTime, parseTime, TIME_CONSTANTS } from '../../../utils/time'
import { DAY_ORDER_SHORT, safeDayIndex, safeShortToFull, safeFullToShort, getNextDay } from '../../../utils/constants'

/**
 * @method mapCustomToSchemaOrg
 * @description - maps the custom format to the schema.org format.
 *   This implementation merges overnight parts so that an interval that is split
 *   over two custom entries (one with a late “open” and one starting at midnight)
 *   will be output as a single entry on the day it starts.
 *   Also, when an interval is declared for several days consecutively,
 *   it is only output for the last day.
 * @param regularHours - the regular opening hours in the custom format.
 * @return - the regular opening hours in the schema.org format.
 */
// Helper function to merge overnight intervals in custom format
function mergeCustomOvernightIntervals(customEntries: CustomEntry[]): CustomEntry[] {
  const mergedEntries: CustomEntry[] = []
  // Merge overnight intervals that are split across two entries
  for (let i = 0; i < customEntries.length; i++) {
    const entryA = customEntries[i]
    // Only consider entries that likely represent an evening portion (ends at 23:59)
    if (entryA.open > 0 && entryA.close === TIME_CONSTANTS.MILLISECONDS_AT_2359) {
      // Iterate over a shallow copy because we mutate entryA.days while merging
      const eveningDays = [...entryA.days]
      eveningDays.forEach(dayA => {
        const nextDay = getNextDay(dayA as any)
        for (let j = 0; j < customEntries.length; j++) {
          if (i === j) continue
          const entryB = customEntries[j]
          // Candidate for an overnight continuation: starts at 00:00
          if (entryB.open === 0 && entryB.days.includes(nextDay)) {
            mergedEntries.push({
              days: [dayA],
              open: entryA.open,
              close: entryB.close
            })
            // Remove the day from both entries so they don't render twice
            entryA.days = entryA.days.filter(day => day !== dayA)
            entryB.days = entryB.days.filter(day => day !== nextDay)
            break
          }
        }
      })
    }
  }
  return mergedEntries
}

function groupConsecutiveDays(entry: CustomEntry): string[][] {
  // Sort the days in ascending order.
  const sortedDays = entry.days.sort((a, b) => safeDayIndex(a) - safeDayIndex(b))
  // Group sorted days into consecutive blocks.
  let groups: string[][] = []
  let currentGroup: string[] = []
  
  sortedDays.forEach((day, index) => {
    if (currentGroup.length === 0) {
      currentGroup.push(day)
    } else {
      const lastDay = currentGroup[currentGroup.length - 1]
      if (safeDayIndex(day) === safeDayIndex(lastDay) + 1) {
        currentGroup.push(day)
      } else {
        groups.push(currentGroup)
        currentGroup = [day]
      }
    }
    if (index === sortedDays.length - 1) {
      groups.push(currentGroup)
    }
  })
  
  return groups
}

function ensureAllDaysHaveEntries(schemaEntries: any[]): any[] {
  // STEP 4: Ensure every day of the week has an entry.
  DAY_ORDER_SHORT.forEach(day => {
    const fullDay = safeShortToFull(day)
    const exists = schemaEntries.some(entry => entry.dayOfWeek === fullDay)
    if (!exists) {
      schemaEntries.push({
        dayOfWeek: fullDay,
        opens: TIME_CONSTANTS.MIDNIGHT_00H,
        closes: TIME_CONSTANTS.MIDNIGHT_00H
      })
    }
  })
  
  return schemaEntries
}

// Represent a custom entry with its applicable day keys.
type CustomEntry = {
  days: string[] // e.g. ['mon', 'wed']
  open: number
  close: number
}

export function mapCustomToSchemaOrg(regularHours: any[]): any[] {
  // Convert raw custom entries into our structured format.
  const customEntries: CustomEntry[] = regularHours.map(item => {
    const days = DAY_ORDER_SHORT.filter(day => !!item[day])
    return {
      days,
      open: item.open,
      close: item.close
    }
  })

  // We'll build a new list of processed entries.
  const processedEntries: CustomEntry[] = []
  
  // STEP 1: Merge overnight intervals using helper function
  const mergedEntries = mergeCustomOvernightIntervals(customEntries)

  // STEP 2: Add remaining entries for days that still need to be rendered.
  customEntries.forEach(entry => {
    if (entry.days.length > 0) {
      processedEntries.push(entry)
    }
  })
  // Add the merged overnight entries.
  processedEntries.push(...mergedEntries)

  // STEP 3: For each processed entry, if it applies to multiple days,
  // split the days into groups of consecutive days and assign the interval to all days in each group.
  const schemaEntries: any[] = []

  processedEntries.forEach(entry => {
    // Group days into consecutive blocks using helper function
    const groups = groupConsecutiveDays(entry)
    
    // For each group, assign the interval to ALL days in the group, not just the last day
    groups.forEach(group => {
      group.forEach(dayKey => {
        schemaEntries.push({
          dayOfWeek: safeShortToFull(dayKey),
          opens: formatTime(entry.open),
          closes: formatTime(entry.close)
        })
      })
    })
  })

  // STEP 4: Ensure every day of the week has an entry using helper function
  ensureAllDaysHaveEntries(schemaEntries)

  // Finally, sort the output by day order (Monday through Sunday)
  // and for the same day, sort by opens time.
  schemaEntries.sort((a, b) => {
    const dayA = safeDayIndex(safeFullToShort(a.dayOfWeek))
    const dayB = safeDayIndex(safeFullToShort(b.dayOfWeek))
    if (dayA === dayB) {
      return a.opens.localeCompare(b.opens)
    }
    return dayA - dayB
  })

  console.log('result from mapCustomToSchemaOrg:', JSON.stringify(schemaEntries))
  return schemaEntries
}
  
 /**
 * @method mapSchemaOrgToCustom
 * @description - maps the schema.org format to the custom format.
 *   This version fixes two issues:
 *     1. For overnight intervals (where openTime >= closeTime), it creates two sub‑entries—
 *        one for the evening part and one for the early‑morning part. The early‑morning part
 *        is merged so that both the original day and its next day are marked.
 *     2. It merges entries with identical open/close times so that consecutive days are combined.
 * @param regularHours - the regular opening hours in the schema.org format.
 * @return - the regular opening hours in the custom format.
 */
export function mapSchemaOrgToCustom(regularHours: any[]): any[] {
  let result: any[] = []

  regularHours.forEach((item: { dayOfWeek: string; opens: string; closes: string }) => {
    const dayKey = safeFullToShort(item.dayOfWeek)
    const openTime = parseTime(item.opens)
    const parsedCloseTime = parseTime(item.closes)
    const closeTime = parsedCloseTime === TIME_CONSTANTS.MILLISECONDS_PER_DAY
      ? TIME_CONSTANTS.MILLISECONDS_AT_2359
      : parsedCloseTime

      // Skip days that are closed.
      if (item.opens === '00:00' && item.closes === '00:00') {
        return
      }

      // Defensive check: ensure we have valid times
      if (isNaN(openTime) || isNaN(closeTime)) {
        console.error(`ERROR: Invalid time parsing for ${item.dayOfWeek}: ${item.opens}-${item.closes}`)
        return
      }
      
      if (openTime < closeTime) {
        // Normal interval (e.g., 08:00-22:00)
        // Try to merge with any existing entry with matching times.
        const existingEntry = result.find(entry => entry.open === openTime && entry.close === closeTime)
        if (existingEntry) {
          existingEntry[dayKey] = true
        } else {
          const newEntry: any = {
            mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false,
            open: openTime,
            close: closeTime
          }
          newEntry[dayKey] = true
          result.push(newEntry)
        }
      } else if (openTime === closeTime) {
        // Edge case: same open and close time - treat as closed
        return
      } else {
       // Overnight interval (e.g., "Wednesday" 23:00 bis "04:00").
      // Erstelle zunächst den "Abend"-Teil: von openTime bis 23:59 (86340000).
      const existingEvening = result.find(
        entry => entry.open === openTime && entry.close === TIME_CONSTANTS.MILLISECONDS_AT_2359
      )
      if (existingEvening) {
        existingEvening[dayKey] = true
      } else {
        const newEvening: any = {
          mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false,
          open: openTime,
          close: TIME_CONSTANTS.MILLISECONDS_AT_2359
        }
        newEvening[dayKey] = true
        result.push(newEvening)
      }
      // Erstelle den Fortsetzungs-Teil für die Übernachtung.
      // Hier soll NUR der nächste Tag gesetzt werden.
      const nextDayKey = getNextDay(dayKey as any) // dayKey is validated from API
      const existingOvernight = result.find(
        entry => entry.open === 0 && entry.close === closeTime
      )
      if (existingOvernight) {
        existingOvernight[nextDayKey] = true
      } else {
        const newOvernight: any = {
          mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false,
          open: 0,
          close: closeTime
        }
        newOvernight[nextDayKey] = true
        result.push(newOvernight)
      }
      }
    })
  
    // After processing all schema.org entries, merge any entries that share the same open/close times.
    const mergedMap = new Map<string, any>()
    result.forEach(entry => {
      const key = `${entry.open}-${entry.close}`
      if (mergedMap.has(key)) {
        const existing = mergedMap.get(key)
        for (const d of DAY_ORDER_SHORT) {
          existing[d] = existing[d] || entry[d]
        }
      } else {
        mergedMap.set(key, { ...entry })
      }
    })
    const mergedResult = Array.from(mergedMap.values())
  
    console.log('result from mapSchemaOrgToCustom:', JSON.stringify(mergedResult))
    return mergedResult
  }
  
